import React, { useState } from 'react';

/**
 * Log-after-the-fact AND correction dialog.
 *
 * `mode="log"`  → a manual entry (fn_effort_log). Title "Log time".
 * `mode="correct"` → editing an existing entry. Title "Edit entry". The person
 *   only ever sees "Edit" — the fact that a correction writes a NEW row server-
 *   side (fn_effort_correct, original preserved) is never surfaced.
 *
 * A non-empty description is required in BOTH modes: fn_effort_log raises on an
 * empty description, and Save stays disabled here until one is present so the
 * raise never happens. Minutes must be a positive number.
 *
 * A manual log also attributes the entry to a client and a service so it lands
 * in the per-client substitution curve — `fn_effort_log` takes `p_client_id` /
 * `p_service`. Both are optional; an unselected value is emitted as `null`, never
 * "". A correction keeps the original entry's attribution (`fn_effort_correct`
 * takes only minutes + description), so those fields show only in `mode="log"`.
 *
 * Emits `{ minutes, description }` (correct) or
 * `{ minutes, description, when, clientId, service }` (log); the page combines
 * them with the entry id (correct) or the range (log) and calls the right RPC.
 *
 * @param {object}  props
 * @param {"log"|"correct"} [props.mode]
 * @param {object}  [props.entry]  The entry being corrected (mode="correct").
 * @param {{id:string,name:string}[]} [props.clients]  Selectable clients (log mode).
 * @param {(values:object)=>void} props.onSubmit
 * @param {()=>void} props.onClose
 * @param {boolean} [props.saving]
 * @param {string}  [props.error]
 */
export default function LogEffortDialog({
  mode = 'log',
  entry = null,
  clients = [],
  onSubmit,
  onClose,
  saving = false,
  error,
}) {
  const correcting = mode === 'correct';
  const initialMinutes = correcting && entry?.minutes != null
    ? String(entry.minutes)
    : '';

  const [minutes, setMinutes] = useState(initialMinutes);
  const [description, setDescription] = useState(correcting ? (entry?.what || '') : '');
  const [when, setWhen] = useState('');
  const [clientId, setClientId] = useState('');
  const [service, setService] = useState('');

  const minutesNum = Number(minutes);
  const descriptionOk = description.trim().length > 0;
  const minutesOk = Number.isFinite(minutesNum) && minutesNum > 0;
  const canSave = descriptionOk && minutesOk && !saving;

  const submit = () => {
    if (!canSave) return;
    onSubmit?.(
      correcting
        ? { minutes: minutesNum, description: description.trim() }
        : {
            minutes: minutesNum,
            description: description.trim(),
            when: when || null,
            clientId: clientId || null,       // "" (no client) → null, not a blank id
            service: service.trim() || null,
          },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" role="dialog" aria-modal="true" aria-label={correcting ? 'Edit entry' : 'Log time'}>
      <div className="w-full md:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl md:rounded-2xl shadow-xl border dark:border-gray-800 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {correcting ? 'Edit entry' : 'Log time'}
        </h2>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minutes</label>
            <input
              data-testid="log-minutes"
              type="number"
              min="1"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
            />
          </div>
          {!correcting && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">When (optional)</label>
              <input
                data-testid="log-when"
                type="date"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
              />
            </div>
          )}
        </div>

        {!correcting && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client (optional)</label>
              <select
                data-testid="log-client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
              >
                <option value="">— No client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Service (optional)</label>
              <input
                data-testid="log-service"
                type="text"
                value={service}
                onChange={(e) => setService(e.target.value)}
                placeholder="e.g. web, ads, content"
                className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
              />
            </div>
          </div>
        )}

        <label className="block mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Description
        </label>
        <textarea
          data-testid="log-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
          placeholder="In your own words…"
        />

        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            data-testid="log-save"
            className="text-sm font-medium rounded-lg bg-[#32B1FF] hover:bg-[#1a9ce6] text-white px-4 py-2 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
